import type { QuestionDefinition } from "@reno-estimator/core";
import { OptionCard } from "./OptionCard.js";

interface QuestionRendererProps {
  question: QuestionDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
}

/**
 * Renders one QuestionDefinition using the same large-touch-target card
 * controls the rest of the wizard uses, dispatching on `inputType`. This
 * is the piece that makes the wizard reusable across project types: a
 * project type's questions (packages/config/src/questions/<type>.ts) are
 * plain data, and this component (plus ProjectSpecificQuestions, which
 * loops over them) is the only code that has to understand how to render
 * that data - no step component needs to know a given project type's
 * question ids ahead of time.
 *
 * Deliberately does not handle "dimension" or "location" inputTypes -
 * those get dedicated, richer step UIs (Step2RoomSize, Step5Location)
 * rather than a generic card renderer, since they need custom controls
 * (a slider, a province/city/postal group) that don't generalize well.
 * "number" (added in Phase 12, for a plain numeric answer like a bedroom
 * count) renders as a simple bounded number input.
 */
export function QuestionRenderer({ question, value, onChange }: QuestionRendererProps) {
  switch (question.inputType) {
    case "single_select": {
      return (
        <fieldset className="detail-field">
          <legend>
            {question.prompt}
            {!question.required ? " (optional)" : ""}
          </legend>
          {question.helpText ? <p className="question-help">{question.helpText}</p> : null}
          <div className="option-grid option-grid--compact" role="radiogroup" aria-label={question.prompt}>
            {(question.options ?? []).map((opt) => (
              <OptionCard
                key={opt.id}
                name={question.id}
                value={opt.id}
                label={opt.label}
                description={opt.description}
                checked={value === opt.id}
                onChange={(checked) => checked && onChange(opt.id)}
              />
            ))}
          </div>
        </fieldset>
      );
    }

    case "multi_select": {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset className="detail-field">
          <legend>
            {question.prompt}
            {!question.required ? " (optional)" : ""}
          </legend>
          {question.helpText ? <p className="question-help">{question.helpText}</p> : null}
          <div className="option-grid option-grid--compact" role="group" aria-label={question.prompt}>
            {(question.options ?? []).map((opt) => (
              <OptionCard
                key={opt.id}
                name={`${question.id}_${opt.id}`}
                value={opt.id}
                label={opt.label}
                description={opt.description}
                type="checkbox"
                checked={selected.includes(opt.id)}
                onChange={(checked) => {
                  const next = checked ? [...selected, opt.id] : selected.filter((id) => id !== opt.id);
                  onChange(next);
                }}
              />
            ))}
          </div>
        </fieldset>
      );
    }

    case "boolean": {
      return (
        <fieldset className="detail-field">
          <legend>
            {question.prompt}
            {!question.required ? " (optional)" : ""}
          </legend>
          {question.helpText ? <p className="question-help">{question.helpText}</p> : null}
          <div className="option-grid option-grid--compact">
            <OptionCard
              name={question.id}
              value="yes"
              label="Yes"
              type="checkbox"
              checked={value === true}
              onChange={(checked) => onChange(checked)}
            />
          </div>
        </fieldset>
      );
    }

    case "text": {
      return (
        <label className="text-field">
          <span>
            {question.prompt}
            {!question.required ? " (optional)" : ""}
          </span>
          <input type="text" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />
        </label>
      );
    }

    case "number": {
      return (
        <label className="text-field">
          <span>
            {question.prompt}
            {!question.required ? " (optional)" : ""}
          </span>
          {question.helpText ? <p className="question-help">{question.helpText}</p> : null}
          <input
            type="number"
            min={question.min}
            max={question.max}
            value={typeof value === "number" ? value : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                onChange(undefined);
                return;
              }
              const parsed = Number(raw);
              if (!Number.isNaN(parsed)) onChange(parsed);
            }}
          />
        </label>
      );
    }

    default:
      // dimension / location aren't asked through this generic renderer today - see the doc comment above.
      return null;
  }
}
